import { useState } from "react";
import { UseFormReset } from "react-hook-form";
import { taskService } from "@miplatform/api";
import {
  ASSIGNMENT_TEMPLATE_TYPES,
  HTTP_STATUSES,
} from "@miplatform/constants";
import { useComponentState } from "@miplatform/hooks";
import {
  IWorkOrderTemplateDetailsResponse,
  IWorkOrderTemplateListItemResponse,
  PlatformModules,
} from "@miplatform/types";
import { createUUID } from "@miplatform/utils";
import { IWorkOrderTemplateData } from "../../views/Config/WorkOrderTemplates/ConfigWorkOrderTemplates.types";

const getWorkOrderTemplateUUID = createUUID();

export const useWorkOrderTemplate = ({
  module = null,
}: IUseWorkOrderTemplate = {}) => {
  const [workOrderTemplateDetails, setWorkOrderTemplateDetails] =
    useState<IWorkOrderTemplateDetailsResponse | null>(null);

  const stateManager = useComponentState();

  const getWorkOrderTemplateDetails = (
    id: string,
    callback?: (data: IWorkOrderTemplateDetailsResponse) => void
  ) => {
    stateManager.abort(getWorkOrderTemplateUUID);
    return stateManager.run(async () => {
      const response = await taskService.workOrderTemplates.getById({ id });
      if (response.status === HTTP_STATUSES.OK) {
        setWorkOrderTemplateDetails(response.data);
        if (callback) {
          callback(response.data);
        }
      }
    }, getWorkOrderTemplateUUID);
  };

  const createWorkOrderTemplate = (data: IWorkOrderTemplateData) => {
    const modifiedData = {
      ...data,
      type: ASSIGNMENT_TEMPLATE_TYPES.INSTALL,
    };

    return stateManager.run(async () => {
      return taskService.workOrderTemplates.create({
        data: modifiedData,
        toastMessageConfig: {
          success: {
            text: `${data.name} was created.`,
          },
        },
      });
    });
  };

  const updateWorkOrderTemplate = (
    data: Partial<IWorkOrderTemplateData>,
    name?: string,
    callback?: (data: IWorkOrderTemplateDetailsResponse) => void
  ) => {
    const modifiedData = {
      ...data,
      type: ASSIGNMENT_TEMPLATE_TYPES.INSTALL,
    };

    return stateManager.run(async () => {
      const response = await taskService.workOrderTemplates.update({
        id: modifiedData.id,
        data: modifiedData,
        toastMessageConfig: {
          success: {
            text: `${name ?? data.name} was updated.`,
          },
        },
      });

      if (response.status === HTTP_STATUSES.OK) {
        setWorkOrderTemplateDetails(response.data);
        if (callback) {
          callback(response.data);
        }
      }
    });
  };

  const deleteWorkOrderTemplate = (
    data: IWorkOrderTemplateListItemResponse
  ) => {
    return stateManager.run(async () => {
      const response = await taskService.workOrderTemplates.delete({
        id: data.id,
        modifiedOn: data.modifiedOn,
        toastMessageConfig: {
          success: {
            text: `${data.name} was deleted successfully.`,
          },
        },
      });
      return response;
    });
  };

  return {
    isLoading: stateManager.isBusy(),
    workOrderTemplateDetails,
    getWorkOrderTemplateDetails,
    createWorkOrderTemplate,
    updateWorkOrderTemplate,
    deleteWorkOrderTemplate,
  };
};

interface IUseWorkOrderTemplate {
  reset?: UseFormReset<IWorkOrderTemplateData>;
  module?: PlatformModules | null;
}
